import React from 'react';
import { ChangelogModel } from './useChangelogData';
import { CATEGORIES } from './changelogTypes';
import { AdminModal } from '../../pages/admin/ConfirmationDialogs';

const fieldStyle: React.CSSProperties = {
  backgroundColor: 'rgba(255,255,255,0.05)',
  color: 'var(--rp-text)',
  border: '1px solid rgba(255,255,255,0.15)',
};

const ChangelogDialogs: React.FC<{ model: ChangelogModel }> = ({ model }) => {
  const {
    showForm, setShowForm, editingId, setEditingId, form, setForm, saving,
    deleteConfirm, setDeleteConfirm, buildVersions, handleSave, handleDelete,
  } = model;

  const closeForm = () => { setShowForm(false); setEditingId(null); };

  return (
    <>
      {/* Create/Edit Modal */}
      {showForm && (
        <AdminModal ariaLabel={editingId ? 'Edit changelog entry' : 'New changelog entry'} onCancel={closeForm} maxWidth="32rem">
            <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              {editingId ? 'Edit Entry' : 'New Changelog Entry'}
            </h3>
            <div className="space-y-4">
              {/* Version */}
              <div>
                <label htmlFor="changelog-version" className="block text-sm font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>Build Version</label>
                <div className="flex gap-2">
                  <input id="changelog-version" type="text" value={form.version}
                    onChange={e => setForm(prev => ({ ...prev, version: e.target.value }))}
                    placeholder="Build 95"
                    className="flex-1 px-3 py-2 rounded-lg text-sm"
                    style={fieldStyle}
                    list="version-options"
                  />
                  <datalist id="version-options">
                    {buildVersions.map(v => <option key={v} value={v} />)}
                  </datalist>
                </div>
              </div>
              {/* Category */}
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>Category</label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map(c => (
                    <button key={c.value}
                      onClick={() => setForm(prev => ({ ...prev, category: c.value }))}
                      className="px-3 py-1.5 rounded-lg text-sm font-semibold transition-all"
                      style={{
                        backgroundColor: form.category === c.value ? `${c.color}30` : 'rgba(255,255,255,0.05)',
                        color: form.category === c.value ? c.color : 'var(--rp-text-secondary)',
                        border: `1px solid ${form.category === c.value ? c.color : 'rgba(255,255,255,0.1)'}`,
                      }}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Title */}
              <div>
                <label htmlFor="changelog-title" className="block text-sm font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>Title</label>
                <input id="changelog-title" type="text" value={form.title}
                  onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="What changed?"
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={fieldStyle}
                />
              </div>
              {/* Description */}
              <div>
                <label htmlFor="changelog-description" className="block text-sm font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>Description</label>
                <textarea id="changelog-description" value={form.description}
                  onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what was added, fixed, or changed..."
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg text-sm resize-y"
                  style={fieldStyle}
                />
              </div>
              {/* Publish toggle */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setForm(prev => ({ ...prev, is_published: !prev.is_published }))}
                  className="admin-pubtoggle"
                  aria-label="Toggle publish immediately"
                >
                  <span
                    className="admin-pubtoggle-track"
                    style={{ backgroundColor: form.is_published ? 'var(--admin-success)' : 'var(--admin-neutral-track)' }}
                  >
                    <span
                      className="admin-pubtoggle-knob"
                      style={{ transform: form.is_published ? 'translateX(28px)' : 'translateX(0)' }}
                    />
                  </span>
                </button>
                <span className="text-sm" style={{ color: form.is_published ? 'var(--admin-success)' : 'var(--rp-text-secondary)' }}>
                  {form.is_published ? 'Publish immediately' : 'Save as draft'}
                </span>
              </div>
            </div>
            {/* Actions */}
            <div className="flex gap-3 justify-end mt-6">
              <button onClick={closeForm}
                className="px-4 py-2 rounded-lg text-sm"
                style={{ color: 'var(--rp-text-secondary)' }}
              >
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-50"
                style={{ backgroundColor: 'var(--admin-primary)' }}
              >
                {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </button>
            </div>
        </AdminModal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <AdminModal ariaLabel="Delete changelog entry" onCancel={() => setDeleteConfirm(null)}>
            <h3 className="text-lg font-bold mb-3" style={{ color: 'var(--rp-text)' }}>Delete Entry?</h3>
            <p className="text-sm mb-6" style={{ color: 'var(--rp-text-secondary)' }}>
              This will permanently remove this changelog entry. This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--rp-text-secondary)' }}
              >
                Cancel
              </button>
              <button onClick={() => handleDelete(deleteConfirm)}
                className="admin-btn admin-btn-danger"
              >
                Delete
              </button>
            </div>
        </AdminModal>
      )}
    </>
  );
};

export default ChangelogDialogs;
